import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { http } from '@/lib/ats-https';
import { assertResolvedAtsNotHttpError } from '@/api/ats/atsEnvelope';
import type {
  AnonymizeResponse,
  BulkRequestConsentsPayload,
  ConsentLifecycleResponse,
  ConsentTarget,
} from './types';

const BASE_URL = '/candidate_consents';

// The ATS client resolves 4xx as a fulfilled promise with `{ status, errors }`,
// so every mutation must assert before returning — otherwise onSuccess fires on
// a 422 and shows a false success toast.
const post = async <T>(url: string, body: unknown): Promise<T> => {
  const res = await http.post(url, body);
  // Anonymize answers 202 with no body, which axios hands back as ''. The assert below treats any
  // string body as a non-JSON failure, so an empty one has to short-circuit — otherwise a success
  // throws and the caller shows an error toast.
  if (res === '' || res === null || res === undefined) return undefined as T;
  assertResolvedAtsNotHttpError(res);
  return res as T;
};

export type ConsentLifecycleAction =
  | 'request'
  | 'remind'
  | 'mark_granted'
  | 'mark_not_required'
  | 'withdraw'
  | 'extend';

export type ExtendUnit = 'days' | 'weeks' | 'months' | 'years';

// `extend` sets a custom validity: a duration XOR a never-expiring grant.
export type ConsentExtendPayload =
  | { value: number; unit: ExtendUnit }
  | { no_expiry: true };

const targetBody = (target: ConsentTarget) =>
  'contactId' in target
    ? { contact_id: target.contactId }
    : { ats_application_id: target.atsApplicationId };

const postLifecycleAction = (
  action: ConsentLifecycleAction,
  target: ConsentTarget,
  extend?: ConsentExtendPayload
): Promise<ConsentLifecycleResponse> =>
  post(`${BASE_URL}/${action}`, {
    ...targetBody(target),
    // Only `extend` carries a body beyond the target.
    ...(extend ?? {}),
  });

// Bulk "request consent" (formerly "renew"). Dispatches an async bulk
// activity (request_consent, enum 25); per-item skips surface via the tracker.
const postBulkRequest = ({
  contactIds,
  atsApplicationIds,
}: BulkRequestConsentsPayload): Promise<unknown> =>
  post(`${BASE_URL}/bulk_request`, {
    ...(contactIds?.length && { contact_ids: contactIds }),
    ...(atsApplicationIds?.length && {
      ats_application_ids: atsApplicationIds,
    }),
  });

const postAnonymize = (
  target: ConsentTarget,
  confirm?: boolean
): Promise<AnonymizeResponse> =>
  post(`${BASE_URL}/anonymize`, {
    ...targetBody(target),
    // Wire key is confirm_active_applications (BE contract delta 2 §1); the old
    // `confirm` key is now ignored server-side.
    ...(confirm && { confirm_active_applications: confirm }),
  });

export const CONSENT_ACTIONS_API = {
  postLifecycleAction,
  postBulkRequest,
  postAnonymize,
};

// The anonymize confirm lists the jobs / projects / flows the candidate is on. Only the contact
// page holds those already — the slider and table rows carry a thin contact — so fetch them when
// the caller can't supply them. A destructive confirm must not under-report what it destroys.
export const CONTACT_RECORDS_KEY = (contactId: number) => [
  'contact-records',
  contactId,
];

export const useContactRecords = (contactId: number, enabled: boolean) =>
  useQuery({
    queryKey: CONTACT_RECORDS_KEY(contactId),
    queryFn: async () => {
      const res = await http.get(`/contacts/${contactId}`);
      assertResolvedAtsNotHttpError(res);
      return (res as { data?: unknown })?.data ?? res;
    },
    enabled,
  });

// Surfaces pass the query keys holding the consent block they render from
// (e.g. the drawer's application query) so the indicator refreshes in place.
type HookOptions = { invalidateKeys?: unknown[][] };

const useInvalidate = (invalidateKeys?: unknown[][]) => {
  const queryClient = useQueryClient();
  return () =>
    invalidateKeys?.forEach((queryKey) =>
      queryClient.invalidateQueries({ queryKey })
    );
};

export const useConsentLifecycleAction = ({
  invalidateKeys,
}: HookOptions = {}) => {
  const invalidate = useInvalidate(invalidateKeys);
  return useMutation({
    mutationFn: ({
      action,
      target,
      extend,
    }: {
      action: ConsentLifecycleAction;
      target: ConsentTarget;
      extend?: ConsentExtendPayload;
    }) => postLifecycleAction(action, target, extend),
    onSuccess: invalidate,
  });
};

export const useBulkRequestConsents = ({
  invalidateKeys,
}: HookOptions = {}) => {
  const invalidate = useInvalidate(invalidateKeys);
  return useMutation({
    mutationFn: (payload: BulkRequestConsentsPayload) =>
      postBulkRequest(payload),
    onSuccess: invalidate,
  });
};

export const useAnonymizeCandidate = ({ invalidateKeys }: HookOptions = {}) => {
  const invalidate = useInvalidate(invalidateKeys);
  return useMutation({
    mutationFn: ({
      target,
      confirm,
    }: {
      target: ConsentTarget;
      confirm?: boolean;
    }) => postAnonymize(target, confirm),
    onSuccess: invalidate,
  });
};
